/*
  Free-shipping subscription line — the deferred checkout interceptor.

  This file was inline in snippets/free-shipping-line-config.liquid until the
  2026-09-14 Ruling M asset refactor. The body below is that snippet's
  interceptor, unchanged, except for named reads of values only Liquid can
  produce:

    - the seven render-time cart-seed numbers, now window.__cwFreeShippingSeed
    - CFG.benefitKey, CFG.observedProductId / observedVariantId / observedPlanId,
      now published on the identity mirror by the snippet

  The snippet still renders on EVERY template and still publishes
  window.__cwFreeShipping (CFG) and window.__cwIsFreeShippingLine before this
  asset runs. Both this asset and the observability asset are `defer` tags
  placed BEFORE the vendor.min.js / theme.js module tags, so the capture-phase
  submit interceptor is armed before any drawer or checkout UI exists.

  FS is a subscription line item. Shopify Functions remain the price authority.
  Plan: docs/plans/boops-free-shipping-subscription-line-item-2026-08-20.md.
*/
(function () {
  "use strict";

  var CFG = window.__cwFreeShipping;
  var isFreeShipping = window.__cwIsFreeShippingLine;
  // The snippet always publishes both. If it did not render, there is no
  // identity to defend and the interceptor must stay out of the way.
  if (!CFG || typeof isFreeShipping !== "function") return;
  // Render-time cart snapshot for the zero-request cleanup gates. Checkout
  // never treats it as fresh mutation authority; summarize() replaces it.
  var SEED = window.__cwFreeShippingSeed || {
    subLines: 0, oneTimeLines: 0, count: 0, quantity: 0,
    planOk: false, guardedSubtotal: 0, guardKnown: false
  };
    var interceptorArmed = Boolean(CFG.armed && CFG.variantId && CFG.planId &&
      CFG.eligibleCustomerSellingPlanIds.length > 0);

    var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    // THE ABSOLUTE CEILING, ratified at 4.0 s by master plan §8.81.2 R81-7(b)
    // (free-shipping §9.23.6). Measured from the intercepted checkout intent to
    // the coordinator's submit. NO path may exceed it.
    var TIMEOUT_MS = (typeof window !== "undefined" && window.__CW_FS_TIMEOUT_MS) || 4000;
    // Bounded wait the hard release grants an IN-FLIGHT removal so the deadline
    // cannot fire mid-rollback. It orders the release; it never cancels it.
    // It is carved OUT OF the ceiling, never added to it — see
    // RECONCILE_DEADLINE_MS and the clamp in release().
    var REMOVAL_GRACE_MS = (typeof window !== "undefined" && window.__CW_FS_REMOVAL_GRACE_MS) || 1000;
    // The safety-net timer fires here, leaving exactly REMOVAL_GRACE_MS of the
    // budget for an in-flight removal. Worst case = RECONCILE_DEADLINE_MS +
    // REMOVAL_GRACE_MS = TIMEOUT_MS, not TIMEOUT_MS + REMOVAL_GRACE_MS.
    var RECONCILE_DEADLINE_MS = Math.max(0, TIMEOUT_MS - REMOVAL_GRACE_MS);
    var LEASE_MS = (typeof window !== "undefined" && window.__CW_FS_LEASE_MS) || 15000;
    var RELEASE_LEASE_MS = (typeof window !== "undefined" && window.__CW_FS_RELEASE_LEASE_MS) || 30000;
    var RETURN_GRACE_MS = (typeof window !== "undefined" && window.__CW_FS_RETURN_GRACE_MS) || 250;
    var UNCERTAINTY_TOMBSTONE_MS =
      (typeof window !== "undefined" && window.__CW_FS_UNCERTAINTY_TOMBSTONE_MS) || 120000;
    var UNCERTAINTY_POLL_MS =
      (typeof window !== "undefined" && window.__CW_FS_UNCERTAINTY_POLL_MS) || 5000;
    var CLEANUP_LEASE_MS = Math.min(5000, LEASE_MS);
    var LEASE_KEY = "cw:free_shipping_checkout_lease:v1";
    var UNCERTAINTY_KEY = "cw:free_shipping_uncertainty:v1";
    var LOCK_NAME = "cw:free_shipping_checkout:v1";
    var BROWSING = "BROWSING";
    var PREPARING = "PREPARING";
    var RELEASING = "RELEASING";
    var phase = BROWSING;
    var operationGeneration = 0;
    var cleanupGeneration = 0;
    var activeAttempt = null;
    var releasePermit = null;
    var removalInFlight = null;
    var directCheckoutOwner = null;

    // Direct buy-box navigation bypasses the form interceptor. Share its
    // cleanup lock BEFORE the theme cart mutex, and retain a release lease
    // through navigation so a queued return sweep cannot remove the offer.
    CFG.runDirectCheckout = function (work) {
      var locks = window.navigator && window.navigator.locks;
      if (!locks || typeof locks.request !== "function") {
        return Promise.reject(new Error("offer_cart_coordination_unavailable"));
      }
      return locks.request(LOCK_NAME, { mode: "exclusive" }, function () {
        var prior = leaseSnapshot().lease;
        if (prior && !prior.released) {
          throw new Error("offer_cart_coordination_busy");
        }
        if (prior) releaseOwnedLease(prior.owner, prior.generation);
        var owner = { owner: ownerToken(), generation: ++operationGeneration };
        if (!writeOwnedLease(owner.owner, owner.generation, LEASE_MS, false, false)) {
          throw new Error("offer_cart_coordination_busy");
        }
        directCheckoutOwner = owner;
        returnCheckAfterLease = true;
        ++cleanupGeneration;
        phase = PREPARING;
        var heartbeat;
        function renew() {
          if (directCheckoutOwner !== owner) return;
          writeOwnedLease(owner.owner, owner.generation, LEASE_MS, false, false);
          heartbeat = setTimeout(renew, Math.max(1, Math.floor(LEASE_MS / 3)));
        }
        renew();
        return Promise.resolve().then(work).then(function (value) {
          clearTimeout(heartbeat);
          phase = RELEASING;
          writeOwnedLease(owner.owner, owner.generation, RELEASE_LEASE_MS, false, true);
          // A browser may stop navigation. The lease still fences cleanup,
          // while the page's ordinary cart checkout becomes usable again.
          setTimeout(function () {
            if (directCheckoutOwner === owner && phase === RELEASING) phase = BROWSING;
          }, 0);
          return value;
        }, function (error) {
          clearTimeout(heartbeat);
          releaseOwnedLease(owner.owner, owner.generation);
          directCheckoutOwner = null;
          phase = BROWSING;
          if (returnSweepPending) requestReturnSweep();
          throw error;
        });
      });
    };

    // ---- Diagnostic trace ring (consolidation plan R14, §9.3 step 2) -------
    // OFF unless a harness sets `window.__CW_FS_TRACE === true` BEFORE this
    // script runs (the transport harness does so in diagnostic mode only).
    // Records each return-sweep step outcome so a residue line that survives
    // a fresh page can be explained from the artifact instead of guessed at.
    // Bounded: a long session must not grow this without limit.
    var TRACE_MAX = 50;
    var TRACE_ENABLED = false;
    try { TRACE_ENABLED = window.__CW_FS_TRACE === true; } catch (e) {}
    function trace(step, detail) {
      if (!TRACE_ENABLED) return;
      try {
        var ring = window.__cwFreeShippingTrace || [];
        if (ring.length >= TRACE_MAX) ring = ring.slice(ring.length - TRACE_MAX + 1);
        window.__cwFreeShippingTrace = ring.concat([{
          at: Date.now(),
          step: String(step),
          detail: detail === undefined ? null : detail
        }]);
      } catch (e) {}
    }
    // Bounded retry for the return sweep. Before R14 a non-stale failure
    // (a non-2xx cart read, the cleanup deadline, a failed removal) ended the
    // sweep silently, and nothing retried until a focus or visibility event.
    // The sweep is the only pre-order remover of a transit-only residue line,
    // so it now retries three times, then alerts.
    var SWEEP_RETRY_DELAYS_MS = [2000, 5000, 10000];
    try {
      if (Array.isArray(window.__CW_FS_SWEEP_RETRY_DELAYS_MS)) {
        SWEEP_RETRY_DELAYS_MS = window.__CW_FS_SWEEP_RETRY_DELAYS_MS.slice();
      }
    } catch (e) {}

    function ownerToken() {
      return Date.now().toString(36) + "-" + Math.random().toString(16).slice(2);
    }

    function attemptIsOpen(attempt) {
      return Boolean(attempt && activeAttempt === attempt && !attempt.released &&
        attempt.generation === operationGeneration && monotonic() < attempt.deadlineAt);
    }

    function requireAttempt(attempt, step) {
      if (attemptIsOpen(attempt)) return;
      var error = new Error("stale-free-shipping-attempt:" + step);
      error.staleAttempt = true;
      throw error;
    }

    function cleanupIsOpen(cleanup) {
      if (!cleanup || cleanup.kind !== "cleanup" || cleanup.generation !== cleanupGeneration ||
          phase !== BROWSING || activeAttempt || monotonic() >= cleanup.deadlineAt) return false;
      var snapshot = leaseSnapshot();
      return !snapshot.available || Boolean(snapshot.lease &&
        snapshot.lease.owner === cleanup.owner && snapshot.lease.generation === cleanup.generation);
    }

    function requireOperation(operation, step) {
      if (operation && operation.kind === "cleanup") {
        if (cleanupIsOpen(operation)) return;
        var cleanupError = new Error("stale-free-shipping-cleanup:" + step);
        cleanupError.staleCleanup = true;
        throw cleanupError;
      }
      if (operation && operation.kind === "observer") {
        var observed = leaseSnapshot();
        if (attemptIsOpen(operation.attempt) && observed.lease &&
            observed.lease.owner === operation.owner &&
            observed.lease.generation === operation.generation &&
            (operation.canRemove !== true || observed.lease.released === true)) return;
        var observerError = new Error("stale-free-shipping-observer:" + step);
        observerError.staleAttempt = true;
        throw observerError;
      }
      requireAttempt(operation, step);
      var lease = leaseSnapshot();
      if (!operation.leaseOwned || (lease.available && (!lease.lease ||
          lease.lease.owner !== operation.owner || lease.lease.generation !== operation.generation))) {
        var leaseError = new Error("stale-free-shipping-lease:" + step);
        leaseError.staleAttempt = true;
        throw leaseError;
      }
    }

    function requireWriteOperation(operation, step) {
      if (operation && operation.kind === "observer") {
        if (operation.canRemove === true) {
          requireOperation(operation, step);
          return;
        }
        var observerError = new Error("read-only-free-shipping-observer:" + step);
        observerError.staleAttempt = true;
        throw observerError;
      }
      requireOperation(operation, step);
    }

    // A removal dispatched before release may settle during the bounded
    // removal grace. Accept only that already-started response; every later
    // read, retry, or write still goes through requireOperation and is fenced.
    function requireSettlingWriteOperation(operation, step) {
      if (!operation || operation.kind === "cleanup") {
        if (operation) requireWriteOperation(operation, step);
        return;
      }
      var attempt = operation.kind === "observer" ? operation.attempt : operation;
      var snapshot = leaseSnapshot();
      var sameAttempt = activeAttempt === attempt && attempt.generation === operationGeneration &&
        monotonic() <= attempt.deadlineAt;
      var sameLease = !snapshot.available || Boolean(snapshot.lease &&
        snapshot.lease.owner === operation.owner &&
        snapshot.lease.generation === operation.generation);
      var observerMaySettle = operation.kind !== "observer" ||
        (operation.canRemove === true && snapshot.lease && snapshot.lease.released === true);
      if (sameAttempt && sameLease && observerMaySettle) return;
      var error = new Error("stale-free-shipping-write-settlement:" + step);
      error.staleAttempt = true;
      throw error;
    }

    function operationIsOpen(operation) {
      try {
        requireOperation(operation, "open-check");
        return true;
      } catch (e) {
        return false;
      }
    }

    function remainingOperationMs(operation) {
      if (operation && operation.kind === "cleanup") {
        return cleanupIsOpen(operation) ? Math.max(0, operation.deadlineAt - monotonic()) : 0;
      }
      if (operation && operation.kind === "observer") return remainingAttemptMs(operation.attempt);
      return remainingAttemptMs(operation);
    }

    function remainingAttemptMs(attempt) {
      if (!attempt || attempt.released) return 0;
      return Math.max(0, attempt.deadlineAt - monotonic());
    }

    function parseLease(raw) {
      try {
        var row = JSON.parse(raw || "null");
        if (!row || row.schemaVersion !== 1 || typeof row.owner !== "string" || !row.owner ||
            !Number.isFinite(Number(row.generation)) || !Number.isFinite(Number(row.expiresAt)) ||
            Number(row.expiresAt) <= Date.now()) return null;
        return {
          owner: row.owner,
          generation: Number(row.generation),
          expiresAt: Number(row.expiresAt),
          uncertain: row.uncertain === true,
          released: row.released === true
        };
      } catch (e) {
        return null;
      }
    }

    function leaseSnapshot() {
      try {
        if (!window.localStorage) return { available: false, lease: null };
        return { available: true, lease: parseLease(window.localStorage.getItem(LEASE_KEY)) };
      } catch (e) {
        return { available: false, lease: null };
      }
    }

    function writeOwnedLease(owner, generation, ttlMs, uncertain, released) {
      var snapshot = leaseSnapshot();
      if (!snapshot.available) return true; // storage exceptions continue same-tab
      if (snapshot.lease && snapshot.lease.owner !== owner) return false;
      var row = {
        schemaVersion: 1,
        owner: owner,
        generation: generation,
        expiresAt: Date.now() + Math.max(1, ttlMs),
        uncertain: uncertain === true,
        released: released === true
      };
      try {
        window.localStorage.setItem(LEASE_KEY, JSON.stringify(row));
        var readback = leaseSnapshot();
        return Boolean(readback.available && readback.lease &&
          readback.lease.owner === owner && readback.lease.generation === generation);
      } catch (e) {
        return true; // storage exceptions continue same-tab
      }
    }

    function releaseOwnedLease(owner, generation) {
      var snapshot = leaseSnapshot();
      if (!snapshot.available || !snapshot.lease) return;
      if (snapshot.lease.owner !== owner || snapshot.lease.generation !== generation) return;
      try { window.localStorage.removeItem(LEASE_KEY); } catch (e) {}
    }

    function uncertaintySnapshot() {
      try {
        if (!window.localStorage) return null;
        var raw = window.localStorage.getItem(UNCERTAINTY_KEY);
        var row = JSON.parse(raw || "null");
        if (!row || row.schemaVersion !== 1 || typeof row.owner !== "string" ||
            !Number.isFinite(Number(row.generation)) || !Number.isFinite(Number(row.expiresAt))) {
          return null;
        }
        if (Number(row.expiresAt) <= Date.now()) {
          window.localStorage.removeItem(UNCERTAINTY_KEY);
          return null;
        }
        return {
          owner: row.owner,
          generation: Number(row.generation),
          expiresAt: Number(row.expiresAt)
        };
      } catch (e) {
        return null;
      }
    }

    function writeUncertaintyTombstone(attempt) {
      if (!attempt || attempt.uncertainAdd !== true) return;
      try {
        window.localStorage.setItem(UNCERTAINTY_KEY, JSON.stringify({
          schemaVersion: 1,
          owner: attempt.owner,
          generation: attempt.generation,
          expiresAt: Date.now() + UNCERTAINTY_TOMBSTONE_MS
        }));
      } catch (e) {}
    }

    function clearUncertaintyTombstone(tombstone) {
      if (!tombstone) return;
      var current = uncertaintySnapshot();
      if (!current || current.owner !== tombstone.owner ||
          current.generation !== tombstone.generation) return;
      try { window.localStorage.removeItem(UNCERTAINTY_KEY); } catch (e) {}
    }

    function extendReleaseLease(attempt) {
      if (!attempt) return;
      // A tab that merely observed another tab's lease must never overwrite it
      // or report losing a lease it never owned.
      if (attempt.foreignLease || !attempt.leaseOwned) return;
      // The releasing page schedules its own later truth check only when the
      // response was uncertain. Other already-open tabs learn about every
      // dispatch from the active lease they observed at load/focus.
      if (attempt.uncertainAdd === true) returnCheckAfterLease = true;
      writeUncertaintyTombstone(attempt);
      var extended = writeOwnedLease(
        attempt.owner, attempt.generation, RELEASE_LEASE_MS, attempt.uncertainAdd === true,
        attempt.released === true
      );
      if (!extended) {
        alertOnce("checkout_lease_lost", { generation: attempt.generation });
      }
    }

    function observeForeignLease(attempt, lease) {
      if (!attemptIsOpen(attempt)) return Promise.resolve(null);
      attempt.foreignLease = true;
      var observer = {
        kind: "observer",
        attempt: attempt,
        owner: lease.owner,
        generation: lease.generation,
        canRemove: lease.released === true
      };
      return readCart(observer).then(function (cart) {
        requireOperation(observer, "foreign-lease-readback");
        summary = summarize(cart);
        noteBoxLine(summary);
        if (!isSettled(summary) && summary.lines.length > 0) {
          alertOnce("checkout_lease_unsettled_line", {
            count: summary.count, quantity: summary.quantity, planOk: summary.planOk
          });
          // A released lease means the former owner has stopped all follow-up
          // work. Under the same browser-lock-then-theme-mutex order, removal
          // is now convergent and safer than knowingly carrying a malformed
          // cosmetic line into checkout. An active owner remains read-only.
          if (observer.canRemove) {
            var mutex = window.themeCartMutex;
            if (!mutex || !mutex.enqueue) return null;
            return trackRemoval(mutex.enqueue(function () {
              requireWriteOperation(observer, "released-lease-repair-entry");
              return readCart(observer).then(function (fresh) {
                requireOperation(observer, "released-lease-repair-summary");
                var state = summarize(fresh);
                summary = state;
                if (isSettled(state) || state.lines.length === 0) return null;
                return removeLines(state.lines, observer)
                  .then(function () { return readCart(observer); })
                  .then(function (after) {
                    requireOperation(observer, "released-lease-repair-verify");
                    summary = summarize(after);
                    if (summary.lines.length > 0) throw new Error("released-lease-repair-verification");
                  });
              });
            }));
          }
        }
        return null;
      }, function () { return null; });
    }

    function runWithCheckoutOwnership(attempt, work) {
      function waitForForeignRelease() {
        return new Promise(function (resolve) {
          function poll() {
            if (!attemptIsOpen(attempt)) { resolve(null); return; }
            var current = leaseSnapshot();
            if (!current.lease || current.lease.owner === attempt.owner) {
              resolve({ retry: true });
              return;
            }
            if (current.lease.released === true) {
              resolve({ lease: current.lease });
              return;
            }
            setTimeout(poll, Math.min(25, Math.max(1, remainingAttemptMs(attempt))));
          }
          poll();
        }).then(function (result) {
          if (!result || !attemptIsOpen(attempt)) return null;
          if (result.lease) return observeForeignLease(attempt, result.lease);
          return ownedWork();
        });
      }

      function ownedWork() {
        if (!attemptIsOpen(attempt)) return null;
        var snapshot = leaseSnapshot();
        if (snapshot.lease && snapshot.lease.owner !== attempt.owner) {
          // The localStorage fallback is advisory rather than atomic. Never
          // inspect or mutate the cart while a foreign owner is active: wait
          // only inside this attempt's absolute checkout budget, then either
          // observe a released owner or fail open without cart work.
          if (snapshot.lease.released !== true) return waitForForeignRelease();
          return observeForeignLease(attempt, snapshot.lease);
        }
        if (!writeOwnedLease(attempt.owner, attempt.generation, LEASE_MS, false, false)) {
          var winner = leaseSnapshot().lease;
          if (!winner) return null;
          return winner.released === true ? observeForeignLease(attempt, winner) : waitForForeignRelease();
        }
        attempt.leaseOwned = true;
        requireOperation(attempt, "lease-acquired");
        return work();
      }

      try {
        var locks = window.navigator && window.navigator.locks;
        if (locks && typeof locks.request === "function") {
          var lockOptions = { mode: "exclusive" };
          if (attempt.controller) lockOptions.signal = attempt.controller.signal;
          var lockWork = locks.request(LOCK_NAME, lockOptions, function () {
            attempt.allowInsert = true;
            attempt.browserClass = "web_locks";
            var holdTimer;
            var boundedHold = Promise.race([
              Promise.resolve().then(ownedWork),
              new Promise(function (resolve) {
                holdTimer = setTimeout(function () { resolve(null); }, remainingAttemptMs(attempt));
              })
            ]);
            return boundedHold.then(function (value) {
              clearTimeout(holdTimer);
              return value;
            }, function (error) {
              clearTimeout(holdTimer);
              throw error;
            });
          });
          var deadline = new Promise(function (resolve) {
            setTimeout(function () { resolve(null); }, remainingAttemptMs(attempt));
          });
          return Promise.race([lockWork, deadline]);
        }
      } catch (e) {}
      // localStorage has no atomic compare-and-swap. It remains an advisory
      // fence for reads and idempotent removals, but without Web Locks this
      // attempt is omission-only so two tabs can never race two system adds.
      attempt.allowInsert = false;
      attempt.browserClass = "advisory_no_insert";
      alertOnce("web_locks_unavailable_insertion_omitted");
      return Promise.resolve().then(ownedWork);
    }

    function runWithCleanupOwnership(work) {
      var cleanup = {
        kind: "cleanup",
        owner: "cleanup-" + ownerToken(),
        generation: ++cleanupGeneration,
        deadlineAt: monotonic() + CLEANUP_LEASE_MS,
        controller: typeof AbortController === "function" ? new AbortController() : null
      };
      function ownedCleanup() {
        var snapshot = leaseSnapshot();
        if (snapshot.lease) {
          trace("cleanup:lease-held", {
            owner: snapshot.lease.owner,
            expiresInMs: snapshot.lease.expiresAt - Date.now()
          });
          return false;
        }
        if (!writeOwnedLease(cleanup.owner, cleanup.generation, CLEANUP_LEASE_MS, false, false)) {
          trace("cleanup:lease-write-failed");
          return false;
        }
        try { requireOperation(cleanup, "lease-acquired"); } catch (error) {
          trace("cleanup:lease-lost-at-acquire");
          releaseOwnedLease(cleanup.owner, cleanup.generation);
          return false;
        }
        trace("cleanup:lease-acquired", { deadlineInMs: cleanup.deadlineAt - monotonic() });
        var deadlineTimer;
        var boundedWork = Promise.race([
          Promise.resolve().then(function () { return work(cleanup); }),
          new Promise(function (_resolve, reject) {
            deadlineTimer = setTimeout(function () {
              reject(new Error("free-shipping-cleanup-deadline"));
            }, Math.max(0, cleanup.deadlineAt - monotonic()));
          })
        ]);
        return boundedWork.then(function (value) {
          clearTimeout(deadlineTimer);
          try { if (cleanup.controller) cleanup.controller.abort(); } catch (e) {}
          releaseOwnedLease(cleanup.owner, cleanup.generation);
          return value;
        }, function (error) {
          clearTimeout(deadlineTimer);
          try { if (cleanup.controller) cleanup.controller.abort(); } catch (e) {}
          releaseOwnedLease(cleanup.owner, cleanup.generation);
          throw error;
        });
      }
      try {
        var locks = window.navigator && window.navigator.locks;
        if (locks && typeof locks.request === "function") {
          return locks.request(LOCK_NAME, { mode: "exclusive", ifAvailable: true }, function (lock) {
            if (!lock) {
              trace("cleanup:lock-unavailable");
              return false;
            }
            return ownedCleanup();
          });
        }
      } catch (e) {}
      return Promise.resolve().then(ownedCleanup);
    }

    // ---- Frame identity (RV-2 / RW-3) -------------------------------------
    // A nested instance of this snippet must never mutate the cart. A
    // cross-origin read that THROWS is "not top level": a frame whose identity
    // cannot be read must not sweep.
    function isTopLevelDocument() {
      try {
        return window.top === window.self;
      } catch (crossOrigin) {
        return false;
      }
    }

    // ---- Alert surface ----------------------------------------------------
    // Storefront half of RDX-D30 and of the loud-rollback contract. Two
    // always-on surfaces and no new clientlog event type (that would need an
    // edge scrub-allowlist and dbt change): a DOM CustomEvent any theme
    // listener or pixel can subscribe to, plus console.warn (captured into
    // console_debug when settings.debug_mode_enabled is on) and a readable
    // window array. The DURABLE daily alert is the warehouse coverage
    // detector; what matters most here is that insertion DISARMS.
    // ---- Release-duration telemetry (R81-7(b) / §9.23.6) ------------------
    // The 4.0 s ceiling is RATIFIED (master plan §8.81.2 R81-7(b)); §9.23.6 is
    // amended from 2.0 s to match. A ratified budget nobody measures is just a
    // number in a document, so the release step now records how long it
    // actually took: from the checkout intent this feature PREVENTS from
    // navigating, to the moment the coordinator submits. That is the exact
    // interval §9.23.6 defines the ceiling over — so the soak p95 is read off
    // real releases, not inferred from harness scenarios.
    //
    // Deliberately reuses the alert surface's transport rules: NO new
    // clientlog event type (that would need an edge scrub-allowlist and a dbt
    // change), so this is a bounded readable window array plus a DOM
    // CustomEvent any theme listener or pixel can subscribe to. `monotonic()`
    // prefers performance.now() so a wall-clock adjustment mid-checkout cannot
    // manufacture a negative or absurd duration.
    var RELEASE_LOG_MAX = 50;
    function monotonic() {
      try {
        if (typeof performance !== "undefined" && performance && performance.now) {
          return performance.now();
        }
      } catch (e) {}
      return Date.now();
    }
    function networkClass() {
      try {
        var value = window.navigator && window.navigator.connection &&
          window.navigator.connection.effectiveType;
        return ["slow-2g", "2g", "3g", "4g"].indexOf(value) !== -1 ? value : "unknown";
      } catch (e) {
        return "unknown";
      }
    }
    function releaseResult(attempt, unsafeCount) {
      if (unsafeCount > 0) return "known_system_line_at_ceiling";
      if (attempt.uncertainAdd) return "uncertain_dispatch";
      if (summary.count === 1 && isSettled(summary)) return "correct_line";
      return attempt.eligibility === "eligible" ? "contained_miss" : "not_applicable";
    }
    // Where the label stood when the shopper was let through (I3 benchmark,
    // operator 2026-10-01: measure first, then pick the click deadline).
    // "not_sent" vs "not_needed" on a cart this click never read rests on the
    // tab's last cart view (a cart:change / cart:refresh answer, else the seed).
    function releaseAddState(attempt, result) {
      if (result === "correct_line") return "verified";
      // An accepted add this click then rolled back never reached checkout.
      if (attempt.addAccepted && attempt.rollbackStarted) return "rolled_back";
      if (attempt.addAccepted) return "accepted_unverified";
      if (attempt.addDispatched) return "sent_unconfirmed";
      if (attempt.eligibility === "eligible") return "not_sent";
      if (attempt.eligibility === "unknown" && lineBelongs(lastCartView())) return "not_sent";
      return "not_needed";
    }
    function recordRelease(attempt, path, knownUnsafeLineCount, releaseAction) {
      try {
        var ms = Math.max(0, Math.round(monotonic() - attempt.intentAt));
        var suspensionMs = Math.max(0, Math.round(Number(attempt.mainThreadSuspensionMs) || 0));
        var unsafeCount = Math.max(0, Number(knownUnsafeLineCount) || 0);
        var over = ms > TIMEOUT_MS;
        var result = releaseResult(attempt, unsafeCount);
        var addState = releaseAddState(attempt, result);
        var payload = {
          attemptId: String(attempt.generation),
          ms: ms,
          // Lower-bound timer drift observed when the deadline callback runs.
          // Kept separate so a background-tab/main-thread pause is not
          // misreported as Shopify network time.
          mainThreadSuspensionMs: suspensionMs,
          activeWorkMs: Math.max(0, ms - suspensionMs),
          path: path,
          releaseAction: releaseAction,
          route: attempt.route,
          surface: attempt.surface,
          eligibility: attempt.eligibility,
          result: result,
          addState: addState,
          lineInTime: addState === "verified" || addState === "accepted_unverified",
          errorCategory: attempt.errorCategory,
          browserClass: attempt.browserClass,
          networkClass: networkClass(),
          knownUnsafeLineCount: unsafeCount,
          unsafeRelease: unsafeCount > 0,
          budgetMs: TIMEOUT_MS,
          overBudget: over,
          benefitKey: CFG.benefitKey
        };
        try {
          var log = window.__cwFreeShippingReleases || [];
          // Bounded: a long session must not grow this without limit.
          if (log.length >= RELEASE_LOG_MAX) log = log.slice(log.length - RELEASE_LOG_MAX + 1);
          window.__cwFreeShippingReleases = log.concat([payload]);
        } catch (e) {}
        try {
          document.dispatchEvent(
            new CustomEvent("cw:free_shipping_release", { detail: payload })
          );
        } catch (e) {}
        // Over-budget is the only condition worth a console line; it is the
        // shape that would have to move the ratified ceiling again.
        if (over) {
          try {
            console.warn("[cw:free_shipping] release exceeded budget", payload);
          } catch (e) {}
        }
      } catch (e) {
        /* telemetry must never block or break a checkout */
      }
    }

    var alerted = {};
    function alertOnce(kind, detail) {
      try {
        if (alerted[kind]) return;
        alerted[kind] = true;
        var payload = { kind: kind, benefitKey: CFG.benefitKey, detail: detail || null };
        try {
          window.__cwFreeShippingAlerts = (window.__cwFreeShippingAlerts || []).concat([payload]);
        } catch (e) {}
        try {
          document.dispatchEvent(new CustomEvent("cw:free_shipping_alert", { detail: payload }));
        } catch (e) {}
        try {
          if (window.console && window.console.warn) window.console.warn("cw:free_shipping_alert", kind);
        } catch (e) {}
      } catch (e) {}
    }

    if (CFG.insertionRequested && !interceptorArmed) {
      alertOnce("invalid_free_shipping_configuration", {
        productResolved: CFG.observedProductId === CFG.productId,
        variantResolved: CFG.observedVariantId === CFG.variantId,
        planResolved: CFG.observedPlanId === CFG.planId,
        productRequiresSellingPlan: CFG.productRequiresSellingPlan,
        eligibleCustomerPlanCount: CFG.eligibleCustomerSellingPlanIds.length
      });
    }

    // Registry-eligible CUSTOMER selling plans. An active plan outside this
    // inventory is "unknown" and disarms insertion.
    var ELIGIBLE_PLAN_IDS = (CFG.eligibleCustomerSellingPlanIds || []).map(String);
    function planIsEligible(planId) {
      if (ELIGIBLE_PLAN_IDS.length === 0) return false;
      return ELIGIBLE_PLAN_IDS.indexOf(String(planId == null ? "" : planId)) !== -1;
    }

    function systemTupleIsValid(item) {
      if (!item || String(item.product_id || "") !== String(CFG.productId) ||
          String(item.variant_id || "") !== String(CFG.variantId) ||
          item.requires_shipping !== false) return false;
      var allocation = item.selling_plan_allocation;
      if (!allocation || !allocation.selling_plan ||
          String(allocation.selling_plan.id || "") !== String(CFG.planId)) return false;
      if (!item.properties || item.properties[CFG.property] !== CFG.propertyValue) return false;
      var zeroFields = [item.original_price, item.final_price, item.line_price, item.final_line_price];
      return zeroFields.every(function (value) {
        return Number.isFinite(Number(value)) && Number(value) === 0;
      });
    }

    // Cart summary seeded from Liquid only for zero-request cleanup gating.
    var summary = {
      subLines: SEED.subLines,
      oneTimeLines: SEED.oneTimeLines,
      count: SEED.count,
      quantity: SEED.quantity,
      planOk: SEED.planOk,
      guardedSubtotal: SEED.guardedSubtotal,
      guardKnown: SEED.guardKnown,
      // The Liquid seed cannot evaluate customer-plan eligibility: the
      // generated registry exposes no Liquid membership query. Declared
      // "unknown" so render-time state can never verify a system line.
      planEligibility: "unknown",
      unknownCustomerPlan: false,
      unknownCustomerPlanIds: []
    };

    // Telemetry only (I3): the newest cart this tab has SEEN without reading it,
    // the answer a theme cart:change / cart:refresh carried. A truth read that
    // starts after it supersedes it. Nothing but releaseAddState() reads it.
    var eventCartView = null;
    var eventCartSeq = 0;
    function lastCartView() {
      return eventCartView || summary;
    }
    function noteEventCart(event) {
      var changed = event && event.detail && event.detail.cart;
      if (!changed || !changed.items) return;
      eventCartSeq += 1;
      eventCartView = summarize(changed);
    }

    function isRoutedLine(item) {
      return Boolean(item && item.properties && item.properties._cw_route);
    }

    function summarize(cart, forCleanup) {
      var guardedSubtotal = Number(cart && cart.items_subtotal_price);
      var guardKnown = Number.isFinite(guardedSubtotal) && guardedSubtotal >= 0 &&
        Number.isFinite(CFG.shippingThresholdCents) && CFG.shippingThresholdCents > 0 &&
        Number.isFinite(CFG.firstSubTargetCents) && CFG.firstSubTargetCents > 0;
      var next = {
        subLines: 0, oneTimeLines: 0, count: 0, quantity: 0,
        planOk: false, lines: [], boxOwned: false, boxGifts: 0,
        guardedSubtotal: guardedSubtotal, guardKnown: guardKnown,
        planEligibility: "known", unknownCustomerPlan: false, unknownCustomerPlanIds: []
      };
      var items = (cart && cart.items) || [];
      // A routed (`_cw_route`) FS line belongs to the buy box only while the
      // box's own subscription is still in the cart. Then it is BOX-OWNED:
      // this file never removes it and never adds a second line beside it.
      // Once that subscription has left (shopper removed it, or the line came
      // over from an abandoned box cart), the routed line is an orphan and is
      // cleaned up like any other FS line. 2026-10-02 IAB test: summarize()
      // counted an orphaned routed line while removeLines() refused every
      // routed line, so a one-time cart carried it into checkout.
      var boxSubscription = items.some(function (candidate) {
        return isRoutedLine(candidate) && !isFreeShipping(candidate) &&
          Boolean(candidate.selling_plan_allocation);
      });
      for (var i = 0; i < items.length; i++) {
        var item = items[i] || {};
        if (forCleanup && isRoutedLine(item)) continue;
        if (isFreeShipping(item)) {
          next.count += 1;
          next.quantity += item.quantity || 0;
          // `lines` is exactly what this file may remove.
          if (boxSubscription && isRoutedLine(item)) next.boxOwned = true;
          else next.lines.push(item);
          var freeLinePrice = Number(item.line_price);
          if (!Number.isFinite(freeLinePrice) || freeLinePrice < 0) {
            next.guardKnown = false;
          } else {
            next.guardedSubtotal -= freeLinePrice;
          }
          if (systemTupleIsValid(item)) next.planOk = true;
        } else if (item.selling_plan_allocation && item.requires_shipping === true) {
          var customerPlan = item.selling_plan_allocation.selling_plan;
          var customerPlanId = customerPlan && customerPlan.id;
          var quantity = Number(item.quantity);
          var linePrice = Number(item.line_price);
          if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(linePrice) || linePrice < 0) {
            next.guardKnown = false;
          } else {
            next.subLines += 1;
            if (!planIsEligible(customerPlanId)) {
              next.unknownCustomerPlan = true;
              next.unknownCustomerPlanIds.push(String(customerPlanId == null ? "" : customerPlanId));
            }
            next.guardedSubtotal -= Math.max(0, linePrice - (CFG.firstSubTargetCents * quantity));
          }
        } else {
          next.oneTimeLines += 1;
          // The box's own $0 gift. Read only by boxLineHealthy().
          var giftPrice = Number(item.final_line_price != null ? item.final_line_price : item.line_price);
          if (boxSubscription && isRoutedLine(item) && giftPrice === 0) next.boxGifts += 1;
        }
      }
      if (next.unknownCustomerPlan) {
        alertOnce("unknown_active_selling_plan", { sellingPlanIds: next.unknownCustomerPlanIds.slice() });
      }
      return next;
    }

    // A box-owned line is never removed or duplicated here, but a box cart in
    // the wrong shape (malformed line, a second FS line, or a paid one-time
    // line under the mixed-cart guard) must still be surfaced at the click.
    function boxLineHealthy(state) {
      if (state.count !== 1 || state.quantity !== 1 || state.planOk !== true ||
          state.unknownCustomerPlan === true) return false;
      if (state.oneTimeLines - state.boxGifts <= 0) return true;
      return state.guardKnown === true && state.guardedSubtotal >= CFG.shippingThresholdCents;
    }
    function noteBoxLine(state) {
      if (state.boxOwned === true && !boxLineHealthy(state)) {
        alertOnce("box_line_unsettled", {
          count: state.count, quantity: state.quantity, planOk: state.planOk
        });
      }
    }

    function lineBelongs(state) {
      if (state.subLines === 0) return false;
      // RDX-D30 storefront half: an active customer selling plan outside the
      // registry's eligible inventory DISARMS insertion (and the alert has
      // already been raised in summarize). Removal stays active, so a stale
      // line is still cleaned up.
      if (state.unknownCustomerPlan === true) return false;
      if (state.oneTimeLines === 0) return true;
      return state.guardKnown === true &&
        state.guardedSubtotal >= CFG.shippingThresholdCents;
    }

    // The invariant: the free line exists exactly once, at quantity one, on the
    // emitted plan, IF AND ONLY IF a subscription cart clears the mixed-cart
    // guard. An unknown guard omits the cosmetic line.
    function isSettled(state) {
      // A SEEDED summary cannot evaluate customer-plan eligibility, so it may
      // never call an existing system line "settled". Checkout always replaces
      // this seed with a fresh /cart.js read before using this predicate.
      //
      // BUDGET CONSEQUENCE (X-2 refutation F7, ruled by §8.81.2 R81-7(b)):
      // every armed supported checkout takes preventDefault + a full reconcile.
      // That path's worst case is TIMEOUT_MS
      // (4000) = the RATIFIED 4.0 s of §9.23.6, because the removal grace is
      // carved out of the deadline rather than added after it. The voided 2.0 s
      // is corrected here in place under the §8.81.2 standing rule. The
      // §9.23.36.2 clause 15 p95 measurement MUST still include a
      // seeded-line-present checkout row, not only the empty-seed happy path.
      if (state.planEligibility !== "known" && state.count > 0) return false;
      // The box is the authority for its own line: settled once nothing this
      // file may remove is left beside it.
      if (state.boxOwned === true) return state.lines.length === 0;
      if (lineBelongs(state)) {
        return state.count === 1 && state.quantity === 1 && state.planOk;
      }
      return state.count === 0;
    }

    function readCart(operation) {
      if (operation) requireOperation(operation, "cart-read-start");
      var options = {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      };
      if (operation && operation.controller) options.signal = operation.controller.signal;
      var readSeq = eventCartSeq;
      return fetch(ROOT + "cart.js", options).then(function (response) {
        trace("cart-read", {
          status: response && response.status,
          owner: operation ? operation.kind : null
        });
        if (operation) requireOperation(operation, "cart-read-response");
        if (!response.ok) {
          var readError = new Error("cart-read:" + ((response && response.status) || 0));
          readError.status = (response && response.status) || 0;
          throw readError;
        }
        return response.json();
      }, function (error) {
        trace("cart-read:error", {
          name: String((error && error.name) || ""),
          message: String((error && error.message) || error),
          owner: operation ? operation.kind : null
        });
        throw error;
      }).then(function (cart) {
        if (operation) requireOperation(operation, "cart-read-json");
        if (readSeq === eventCartSeq) eventCartView = null;
        return cart;
      });
    }

    function wait(ms) {
      return new Promise(function (resolve) { setTimeout(resolve, ms); });
    }

    // One 409 retry, matching cart-merchandising.js. Shopify runs simultaneous
    // cart writes concurrently and answers 409 on overload; a single spaced
    // retry clears the common case without turning a busy cart into a storm.
    function postCart(path, body, operation) {
      var options = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(body)
      };
      if (operation && operation.controller) options.signal = operation.controller.signal;
      function send() {
        if (operation) requireWriteOperation(operation, "cart-write-start:" + path);
        return fetch(ROOT + path, options);
      }
      return send().then(function (response) {
        trace("cart-write", {
          path: path,
          status: response && response.status,
          owner: operation ? operation.kind : null
        });
        if (operation) requireSettlingWriteOperation(operation, "cart-write-response:" + path);
        if (response && response.status === 409) {
          if (operation && remainingOperationMs(operation) <= 500) {
            throw new Error("cart-write-retry-out-of-budget:" + path);
          }
          return wait(500).then(send);
        }
        return response;
      }).then(function (response) {
        if (operation) requireSettlingWriteOperation(operation, "cart-write-settled:" + path);
        // RW-11b. A non-2xx cart write must FAIL, never resolve: a rollback
        // that silently did nothing was indistinguishable from one that
        // worked, which is how a quantity-2 system line reached order #3835.
        if (!response || !response.ok) {
          throw new Error("cart-write-failed:" + path + ":" + ((response && response.status) || 0));
        }
        return response;
      });
    }

    function addLine(attempt) {
      try { requireWriteOperation(attempt, "t2-add-build"); } catch (error) { return Promise.reject(error); }
      var fields = {
        id: String(CFG.variantId),
        quantity: "1",
        selling_plan: String(CFG.planId),
        return_to: "/cart.js"
      };
      fields["properties[" + CFG.property + "]"] = String(CFG.propertyValue);
      var body = Object.keys(fields).map(function (name) {
        return encodeURIComponent(name) + "=" + encodeURIComponent(fields[name]);
      }).join("&");
      var options = {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body,
        redirect: "manual",
        credentials: "same-origin"
      };
      if (attempt.controller) options.signal = attempt.controller.signal;

      try { requireWriteOperation(attempt, "t2-add-dispatch"); } catch (error) { return Promise.reject(error); }
      attempt.addDispatched = true;
      return new Promise(function (resolve, reject) {
        var finished = false;
        // Reserve rollback grace and one verification slice. A missing fetch
        // response is uncertain, not failed: the server can still commit after
        // the browser stops waiting. The outer controller remains authoritative
        // for the absolute checkout deadline.
        var responseTimeout = Math.max(5, remainingAttemptMs(attempt) - REMOVAL_GRACE_MS -
          Math.min(250, Math.max(5, TIMEOUT_MS / 16)));
        var timer = setTimeout(function () {
          if (finished) return;
          finished = true;
          attempt.uncertainAdd = true;
          resolve({ dispatched: true, uncertain: true });
        }, responseTimeout);

        fetch(ROOT + "cart/add", options).then(function (response) {
          attempt.addResponseSettled = true;
          if (finished) return;
          finished = true;
          clearTimeout(timer);
          try { requireSettlingWriteOperation(attempt, "t2-add-response"); } catch (error) { reject(error); return; }
          // Chromium exposes a manual redirect as an opaque redirect with
          // status 0. A normal 2xx/3xx response is also an accepted Shopify
          // response. Anything else is a transport refusal and enters rollback.
          var accepted = response && (response.type === "opaqueredirect" ||
            (response.status >= 200 && response.status < 400));
          if (!accepted) {
            var refusal = new Error("t2-add-failed:" + ((response && response.status) || 0));
            refusal.requestDispatched = true;
            reject(refusal);
            return;
          }
          attempt.addAccepted = true;
          resolve({ dispatched: true, uncertain: false });
        }, function (error) {
          if (finished) return;
          finished = true;
          clearTimeout(timer);
          error.requestDispatched = true;
          reject(error);
        });
      });
    }

    // Remove every free-shipping line, one at a time. `change.js` is addressed
    // by line key here rather than by index, so removing one line cannot shift
    // the target of the next.
    function attemptForOperation(operation) {
      if (!operation || operation.kind === "cleanup") return null;
      return operation.kind === "observer" ? operation.attempt : operation;
    }

    function rememberUnsafeLines(operation, lines) {
      var attempt = attemptForOperation(operation);
      if (!attempt) return;
      if (!Array.isArray(attempt.unsafeLineKeys)) attempt.unsafeLineKeys = [];
      (lines || []).forEach(function (line) {
        var key = line && line.key;
        if (key && attempt.unsafeLineKeys.indexOf(key) === -1) attempt.unsafeLineKeys.push(key);
      });
    }

    function forgetUnsafeLine(operation, key) {
      var attempt = attemptForOperation(operation);
      if (!attempt || !Array.isArray(attempt.unsafeLineKeys)) return;
      attempt.unsafeLineKeys = attempt.unsafeLineKeys.filter(function (candidate) {
        return candidate !== key;
      });
    }

    // `lines` always comes from summarize(), which withholds box-owned routed
    // lines (and, for cleanup, every routed line). Filtering `_cw_route` again
    // here made an orphaned routed line count as present yet unremovable.
    function removeLines(lines, operation) {
      lines = lines || [];
      rememberUnsafeLines(operation, lines);
      var pendingRemoval = (lines || []).reduce(function (chain, line) {
        return chain.then(function () {
          if (operation) requireWriteOperation(operation, "remove-line");
          if (!line || !line.key) return null;
          return postCart("cart/change.js", { id: line.key, quantity: 0 }, operation)
            .then(function (response) {
              forgetUnsafeLine(operation, line.key);
              return response;
            });
        });
      }, Promise.resolve()).catch(function (error) {
        if (error && (error.staleAttempt || error.staleCleanup)) throw error;
        alertOnce("system_line_removal_failed", {
          message: String((error && error.message) || error)
        });
        throw error;
      });
      return trackRemoval(pendingRemoval);
    }

    // RW-11a. Unconditional post-add rollback. Never rejects: the caller must
    // release checkout either way, and the failure is surfaced by alertOnce.
    function rollbackSystemLines(attempt) {
      if (attempt && !operationIsOpen(attempt)) return Promise.resolve(null);
      return readCart(attempt).then(function (cart) {
        if (attempt) requireOperation(attempt, "rollback-summary");
        var state = summarize(cart);
        summary = state;
        if (state.lines.length === 0) return null;
        return removeLines(state.lines, attempt).then(function () { return readCart(attempt); }).then(function (after) {
          if (attempt) requireOperation(attempt, "rollback-verify");
          summary = summarize(after);
          if (summary.lines.length > 0) throw new Error("rollback-verification");
        });
      });
    }

    function trackedRollback(attempt) {
      if (attempt && !operationIsOpen(attempt)) return Promise.resolve(null);
      if (attempt) attempt.rollbackStarted = true;   // telemetry only (releaseAddState)
      var pending = rollbackSystemLines(attempt).catch(function (error) {
        if (error && error.staleAttempt) return null;
        alertOnce("system_line_rollback_failed", {
          message: String((error && error.message) || error)
        });
      });
      return trackRemoval(pending);
    }

    function trackRemoval(pending) {
      removalInFlight = pending;
      function clearTrackedRemoval() {
        if (removalInFlight === pending) removalInFlight = null;
      }
      pending.then(clearTrackedRemoval, clearTrackedRemoval);
      return pending;
    }

    // ---- Transit-only return sweep ----------------------------------------
    // The display line is allowed only during the transition into checkout.
    // Every storefront return removes it whether or not a paid subscription
    // remains, so a stale line cannot survive a back-navigation or bfcache
    // restore. The local count gates keep ordinary pageviews at zero requests.
    // An active checkout lease is the sole exception to the local count gate:
    // this view may have rendered before another tab's add, and removing an
    // iframe cannot cancel a request Shopify already received. The next safe
    // storefront view therefore performs one truth read after lease expiry.
    var initialLease = leaseSnapshot().lease;
    var initialUncertainty = uncertaintySnapshot();
    // Any active checkout lease means this view may have rendered just before
    // another tab added the transit-only line. `uncertain` is retained in the
    // record for diagnosis, but both settled and uncertain dispatches need the
    // same one-time return check in an already-open tab.
    var returnCheckAfterLease = Boolean(initialLease || initialUncertainty);
    var returnSweepPending = false;
    var returnSweepTimer = null;
    function scheduleReturnSweep(delayMs) {
      clearTimeout(returnSweepTimer);
      returnSweepTimer = setTimeout(requestReturnSweep, Math.max(0, delayMs));
    }
    function returnLineCanExist() {
      if (leaseSnapshot().lease) returnCheckAfterLease = true;
      if (uncertaintySnapshot()) returnCheckAfterLease = true;
      return summary.count > 0 || returnCheckAfterLease;
    }
    function retryReturnSweepAfterLease() {
      returnSweepPending = true;
      var snapshot = leaseSnapshot();
      var uncertainty = uncertaintySnapshot();
      var delay = snapshot.lease ? Math.max(1, snapshot.lease.expiresAt - Date.now() + 5) :
        (uncertainty ? UNCERTAINTY_POLL_MS : 25);
      scheduleReturnSweep(Math.min(RELEASE_LEASE_MS + 10, delay));
    }
    var sweepFailures = 0;
    function retryReturnSweepAfterFailure(error) {
      var message = String((error && error.message) || error);
      if (sweepFailures >= SWEEP_RETRY_DELAYS_MS.length) {
        trace("sweep:exhausted", { message: message, attempts: sweepFailures });
        alertOnce("system_line_sweep_exhausted", {
          message: message,
          attempts: sweepFailures
        });
        return;
      }
      var delay = SWEEP_RETRY_DELAYS_MS[sweepFailures];
      sweepFailures += 1;
      trace("sweep:retry-scheduled", { message: message, attempt: sweepFailures, delayMs: delay });
      returnSweepPending = true;
      scheduleReturnSweep(delay);
    }
    var consumedPreparedCart = null;
    // Preparation must survive the return-trigger debounce; cap reuse at five seconds.
    // The purchase path clears the shared promise before mutating the cart.
    function firstReturnCart(cleanup) {
      var prepared = window.__cwPreparedCart;
      if (!prepared || typeof prepared.then !== "function" || prepared === consumedPreparedCart) return readCart(cleanup);
      consumedPreparedCart = prepared;
      var deadline;
      var bounded = Promise.race([prepared, new Promise(function (resolve) {
        deadline = setTimeout(function () { resolve(null); }, RETURN_GRACE_MS);
      })]);
      return bounded.then(function (snapshot) {
        requireOperation(cleanup, "return-sweep-prepared");
        var age = snapshot ? Date.now() - snapshot.preparedAt : -1;
        if (window.__cwPreparedCart === prepared && age >= 0 && age <= 5000 &&
            snapshot.cart && Array.isArray(snapshot.cart.items)) return snapshot.cart;
        return readCart(cleanup);
      }, function () { return readCart(cleanup); }).finally(function () { clearTimeout(deadline); });
    }
    function buyboxBusy() {
      // The inline box can buy before theme.js publishes its mutex. Its lock
      // lasts through navigation, so queueing cleanup alone is not sufficient.
      return Boolean(window.__cwBuybox && window.__cwBuybox.busy);
    }
    function requestReturnSweep() {
      // RV-2 / RW-3: a NESTED instance must never sweep — it would delete the
      // line the parent frame just added.
      if (!isTopLevelDocument()) {
        trace("sweep:nested");
        return;
      }
      if (!returnLineCanExist()) {
        trace("sweep:no-line", { count: summary.count });
        return;
      }
      if (phase !== BROWSING || activeAttempt || buyboxBusy()) {
        trace("sweep:deferred", { phase: phase, activeAttempt: Boolean(activeAttempt) });
        returnSweepPending = true;
        return;
      }
      var heldLease = leaseSnapshot().lease;
      if (heldLease) {
        trace("sweep:lease-wait", {
          owner: heldLease.owner,
          expiresInMs: heldLease.expiresAt - Date.now(),
          uncertainty: Boolean(uncertaintySnapshot())
        });
        retryReturnSweepAfterLease();
        return;
      }
      var mutex = window.themeCartMutex;
      if (!mutex || !mutex.enqueue) {
        // theme.js may not have registered the mutex yet on this view; treat
        // it as a transient failure so the bounded retry looks again.
        retryReturnSweepAfterFailure(new Error("theme-cart-mutex-missing"));
        return;
      }
      returnSweepPending = false;
      trace("sweep:start", { count: summary.count, uncertainty: Boolean(uncertaintySnapshot()) });
      runWithCleanupOwnership(function (cleanup) {
        return Promise.resolve().then(function () {
          return mutex.enqueue(function () {
            // A tap may have started after this sweep queued behind theme work.
            if (buyboxBusy()) { returnSweepPending = true; return "buybox-busy"; }
            requireOperation(cleanup, "return-sweep-mutex-entry");
            trace("sweep:mutex-entry");
            return firstReturnCart(cleanup).then(function (cart) {
              var state = summarize(cart, true);
              summary = state;
              var tombstone = uncertaintySnapshot();
              if (state.count === 0) {
                if (tombstone) {
                  trace("sweep:uncertain-empty");
                  returnCheckAfterLease = true;
                  return "uncertain-empty";
                }
                trace("sweep:empty");
                returnCheckAfterLease = false;
                return null;
              }
              trace("sweep:remove", { count: state.count });
              return removeLines(state.lines, cleanup);
            }).then(function (result) {
              return result === "uncertain-empty" ? result : readCart(cleanup);
            }).then(function (cart) {
              if (cart === "uncertain-empty") return cart;
              requireOperation(cleanup, "return-sweep-verify");
              summary = summarize(cart, true);
              trace("sweep:verify", { count: summary.count });
              if (summary.count > 0) throw new Error("return-sweep-verification");
              var tombstone = uncertaintySnapshot();
              clearUncertaintyTombstone(tombstone);
              returnCheckAfterLease = false;
              return null;
            });
          });
        });
      }).then(function (ran) {
        if (ran === "buybox-busy") return;
        if (ran === false) {
          trace("sweep:ownership-unavailable");
          retryReturnSweepAfterLease();
          return;
        }
        if (ran === "uncertain-empty") {
          retryReturnSweepAfterLease();
          return;
        }
        sweepFailures = 0;
        trace("sweep:done", { count: summary.count });
        if (summary.count === 0) document.dispatchEvent(new CustomEvent("cart:refresh"));
      }).catch(function (error) {
        var stale = Boolean(error && (error.staleAttempt || error.staleCleanup));
        trace("sweep:error", { message: String((error && error.message) || error), stale: stale });
        if (stale) {
          retryReturnSweepAfterLease();
          return;
        }
        // R14 / RV-2: a non-stale failure while a line is believed to exist
        // is retried on a bounded schedule instead of waiting for the next
        // focus or visibility event.
        retryReturnSweepAfterFailure(error);
      });
    }

    try {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", function () {
          if (returnLineCanExist()) scheduleReturnSweep(RETURN_GRACE_MS);
        }, { once: true });
      } else if (returnLineCanExist()) {
        scheduleReturnSweep(RETURN_GRACE_MS);
      }
      window.addEventListener("pageshow", function (event) {
        if (event && event.persisted === true && directCheckoutOwner && phase !== PREPARING) {
          releaseOwnedLease(directCheckoutOwner.owner, directCheckoutOwner.generation);
          directCheckoutOwner = null;
          phase = BROWSING;
        }
        if (event && event.persisted === true && returnLineCanExist()) {
          scheduleReturnSweep(RETURN_GRACE_MS);
        }
      });
      window.addEventListener("focus", function () {
        if (returnLineCanExist()) scheduleReturnSweep(RETURN_GRACE_MS);
      });
      document.addEventListener("visibilitychange", function () {
        if (document.visibilityState === "visible" &&
            returnLineCanExist()) scheduleReturnSweep(RETURN_GRACE_MS);
      });
    } catch (e) {}

    function normalizeMergedLine(state, attempt) {
      if (!lineBelongs(state) || state.count !== 1 || state.planOk !== true ||
          state.quantity <= 1 || !state.lines[0] || !state.lines[0].key) {
        return trackedRollback(attempt);
      }
      rememberUnsafeLines(attempt, state.lines);
      return trackRemoval(postCart("cart/change.js", { id: state.lines[0].key, quantity: 1 }, attempt)
        .then(function () {
          forgetUnsafeLine(attempt, state.lines[0].key);
          return readCart(attempt);
        })
        .then(function (cart) {
          requireOperation(attempt, "merged-line-verify");
          summary = summarize(cart);
          if (!isSettled(summary)) return trackedRollback(attempt);
          return null;
        }));
    }

    function reconcile(attempt) {
      var addAttempted = false;
      var needsVerification = false;
      return readCart(attempt).then(function (cart) {
        requireOperation(attempt, "reconcile-summary");
        var state = summarize(cart);
        summary = state;
        noteBoxLine(state);
        attempt.eligibility = lineBelongs(state) ? "eligible" : "not_eligible";
        if (isSettled(state)) return null;
        if (state.boxOwned === true || !lineBelongs(state)) {
          needsVerification = state.lines.length > 0;
          return removeLines(state.lines, attempt);
        }
        if (state.count === 0) {
          if (attempt.allowInsert !== true) return null;
          addAttempted = true;
          needsVerification = true;
          return addLine(attempt);
        }
        // Present but wrong (duplicated, wrong quantity, or wrong plan):
        // clear it out and re-add one clean line.
        needsVerification = true;
        return removeLines(state.lines, attempt).then(function () {
          requireOperation(attempt, "re-add-after-removal");
          addAttempted = true;
          return addLine(attempt);
        });
      }).then(function () {
        if (!needsVerification) return null;
        requireOperation(attempt, "post-reconcile-read");
        return readCart(attempt);
      }).then(function (cart) {
        if (!needsVerification) return null;
        requireOperation(attempt, "post-reconcile-summary");
        var state = summarize(cart);
        summary = state;
        if (isSettled(state)) return null;
        if (lineBelongs(state) && state.count === 1 && state.planOk && state.quantity > 1) {
          return normalizeMergedLine(state, attempt);
        }
        // Verification failed. A missing line is cosmetic; a wrong one would
        // reach the customer's order. Roll back and let checkout proceed.
        if (state.lines.length === 0) return null;
        return trackedRollback(attempt);
      }, function (error) {
        if ((error && error.staleAttempt) || !operationIsOpen(attempt)) return null;
        // RW-11a. A REJECTING post-add verification read used to skip the
        // rollback entirely, so a wrong line could reach a paid order. Any
        // failure after an attempted add now rolls back first. The error is
        // absorbed: the caller releases checkout either way.
        if (!addAttempted && !attempt.addDispatched) return null;
        return trackedRollback(attempt);
      });
    }

    function checkoutContext(event) {
      var form = event && event.target;
      if (!form || form.tagName !== "FORM") return null;
      var submitter = event.submitter || null;
      if (submitter && submitter.getAttribute && submitter.getAttribute("name") === "checkout") {
        return { form: form, submitter: submitter };
      }
      if (submitter) return null;
      var action = "";
      try { action = String((form.getAttribute && form.getAttribute("action")) || form.action || ""); } catch (e) {}
      if (action.indexOf("/cart") === -1) return null;
      var checkout = null;
      try { checkout = form.querySelector && form.querySelector('[name="checkout"]'); } catch (e) {}
      if (!checkout) return null;
      return { form: form, submitter: checkout };
    }

    function permitMatches(context) {
      if (!releasePermit || !context) return false;
      return releasePermit.generation === operationGeneration &&
        releasePermit.form === context.form && releasePermit.submitter === context.submitter;
    }

    function directCheckoutNavigation() {
      var destination = ROOT + "checkout";
      try {
        if (window.location && typeof window.location.assign === "function") {
          window.location.assign(destination);
          return true;
        }
      } catch (e) {}
      try {
        if (window.location) {
          window.location.href = destination;
          return true;
        }
      } catch (e) {}
      try {
        var checkoutLink = document.createElement("a");
        checkoutLink.href = destination;
        checkoutLink.hidden = true;
        document.body.appendChild(checkoutLink);
        checkoutLink.click();
        return true;
      } catch (e) {}
      alertOnce("checkout_release_failed", { destination: destination });
      return false;
    }

    function coordinatorErrorCategory(error) {
      var message = String((error && error.message) || error || "unknown");
      if (error && (error.staleAttempt || error.staleCleanup)) return "stale_generation";
      if (/deadline|timeout|abort/i.test(message)) return "deadline_or_abort";
      if (/409|conflict/i.test(message)) return "conflict";
      if (/cart-write|cart-read|native-form|fetch|network/i.test(message)) return "cart_transport";
      return "unexpected";
    }

    // ---- Capture-phase checkout interceptor -------------------------------
    try {
      document.addEventListener("submit", function (event) {
        var attempt = null;
        var release = null;
        var intercepted = false;
        try {
          if (!interceptorArmed) return;
          var context = checkoutContext(event);
          if (!context) return;
          if (permitMatches(context)) {
            releasePermit = null; // exact one-use coordinator authority
            return;
          }
          if (directCheckoutOwner && phase !== BROWSING) {
            event.preventDefault();
            return;
          }
          if (activeAttempt) {
            event.preventDefault();
            // Suppress analytics for this joined gesture. The customer's first
            // intent already produced the one checkout event.
            try { window.__cwFreeShippingJoinedSubmit = event; } catch (e) {}
            return;
          }
          var submitter = context.submitter;
          var form = context.form;

          var mutex = window.themeCartMutex;
          if (!mutex || !mutex.enqueue) return; // theme.js absent: stand down

          event.preventDefault();
          intercepted = true;
          // R81-7(b) / §9.23.6: the ratified 4.0 s ceiling is measured from
          // exactly HERE — the first checkout intent the feature prevents from
          // navigating — to the coordinator's submit.
          var intentAt = monotonic();
          attempt = {
            generation: ++operationGeneration,
            owner: ownerToken(),
            phase: PREPARING,
            intentAt: intentAt,
            deadlineAt: intentAt + TIMEOUT_MS,
            released: false,
            addDispatched: false,
            addResponseSettled: false,
            uncertainAdd: false,
            foreignLease: false,
            leaseOwned: false,
            allowInsert: false,
            unsafeLineKeys: [],
            route: (window.location && window.location.pathname) || "unknown",
            surface: (submitter.closest && submitter.closest("cart-drawer")) ? "cart_drawer" : "cart",
            eligibility: "unknown",
            errorCategory: null,
            browserClass: "unknown",
            mainThreadSuspensionMs: 0,
            controller: typeof AbortController === "function" ? new AbortController() : null
          };
          activeAttempt = attempt;
          phase = PREPARING;

          // THE LINE IS COSMETIC AND MUST NEVER BLOCK A PURCHASE. The release
          // is never cancelled by a rollback — it is only ORDERED AFTER an
          // in-flight removal, and that wait is bounded by the budget REMAINING
          // (see remainingBudget()), so the worst case is TIMEOUT_MS: the
          // ratified 4.0 s, with no additive term.
          // Which path got us to the submit, carried into the telemetry so an
          // over-budget p95 says WHY: reconciliation finished, the safety-net
          // deadline fired, or a rollback wait extended it past TIMEOUT_MS.
          var releasePath = "reconciled";
          var releaseCompletion = null;
          // Milliseconds of the ratified ceiling still unspent. This is what
          // makes the grace SUBTRACTIVE instead of additive: whatever happened
          // before it, the removal wait can never push the submit past
          // intentAt + TIMEOUT_MS.
          function remainingBudget() {
            return Math.max(0, TIMEOUT_MS - (monotonic() - intentAt));
          }
          release = function () {
            if (!attempt || attempt.released) return releaseCompletion || Promise.resolve();
            var expectedDeadlineAt = intentAt + RECONCILE_DEADLINE_MS;
            if (releasePath === "reconciled" && monotonic() >= expectedDeadlineAt) {
              attempt.mainThreadSuspensionMs = Math.max(
                attempt.mainThreadSuspensionMs,
                monotonic() - expectedDeadlineAt
              );
              releasePath = "deadline";
            }
            attempt.released = true;
            attempt.phase = RELEASING;
            phase = RELEASING;
            clearTimeout(timer);
            if (attempt.addDispatched && !attempt.addResponseSettled) {
              attempt.uncertainAdd = true;
              returnCheckAfterLease = true;
            }
            if (attempt.uncertainAdd) releasePath = releasePath + "+uncertain-add";
            extendReleaseLease(attempt);
            var pendingRemoval = removalInFlight;
            if (!pendingRemoval) {
              submitNow();
              releaseCompletion = Promise.resolve();
              return releaseCompletion;
            }
            var graceMs = Math.min(REMOVAL_GRACE_MS, remainingBudget());
            releasePath = releasePath + "+removal-wait";
            function proceed() { submitNow(); }
            // A zero-length wait still resolves — the release always happens.
            releaseCompletion = Promise.race([pendingRemoval, wait(graceMs)]).then(proceed, proceed);
            return releaseCompletion;
          };
          function submitNow() {
            try { if (attempt.controller) attempt.controller.abort(); } catch (e) {}
            var unsafeCount = Array.isArray(attempt.unsafeLineKeys) ? attempt.unsafeLineKeys.length : 0;
            if (unsafeCount > 0) {
              releasePath = releasePath + "+known-system-line";
              alertOnce("checkout_released_with_known_system_line", {
                generation: attempt.generation,
                lineCount: unsafeCount
              });
            }
            var targetForm = form;
            var targetSubmitter = submitter;
            try {
              if (form.isConnected === false) {
                var liveCheckout = document.querySelector('cart-drawer [name="checkout"], form[action*="/cart"] [name="checkout"]');
                var liveForm = liveCheckout && (liveCheckout.form ||
                  (liveCheckout.closest && liveCheckout.closest("form")));
                if (liveForm) {
                  targetForm = liveForm;
                  targetSubmitter = liveCheckout;
                } else {
                  targetForm = null;
                }
              }
            } catch (formResolutionError) {
              targetForm = null;
            }
            var releaseAction = targetForm && typeof targetForm.requestSubmit === "function"
              ? "request_submit" : "direct_navigation";
            // Publish one atomic release fact. The harness must never observe
            // the duration before it can also identify the action that ended
            // the prevented checkout interval.
            if (releaseAction === "request_submit") {
              try { window.__cwFreeShippingResubmit = Date.now(); } catch (e) {}
            }
            // Recorded BEFORE the action: §9.23.6 defines the ceiling as
            // ending when the coordinator invokes requestSubmit()/navigation.
            recordRelease(attempt, releasePath, unsafeCount, releaseAction);
            try {
              if (releaseAction === "request_submit") {
                releasePermit = {
                  form: targetForm,
                  submitter: targetSubmitter,
                  generation: attempt.generation
                };
                // Suppress only the coordinator-generated submit. The
                // customer's original intent remains the analytics event.
                try {
                  targetForm.requestSubmit(targetSubmitter);
                } finally {
                  if (releasePermit && releasePermit.generation === attempt.generation) {
                    releasePermit = null;
                  }
                }
              } else {
                directCheckoutNavigation();
              }
            } catch (submitError) {
              releasePermit = null;
              directCheckoutNavigation();
            }
            setTimeout(function () {
              if (activeAttempt === attempt) activeAttempt = null;
              phase = BROWSING;
              if (returnSweepPending || returnCheckAfterLease) requestReturnSweep();
            }, 0);
          }

          // SAFETY NET (do not remove). Whatever happens below — a hung fetch,
          // a stuck mutex, a thrown error — checkout happens within TIMEOUT_MS.
          // It fires at RECONCILE_DEADLINE_MS so that even the worst case
          // (deadline + a full removal grace) lands on the ceiling, not past it.
          var timer = setTimeout(function () {
            attempt.mainThreadSuspensionMs = Math.max(
              attempt.mainThreadSuspensionMs,
              monotonic() - (intentAt + RECONCILE_DEADLINE_MS)
            );
            releasePath = "deadline";
            if (!attempt.errorCategory) attempt.errorCategory = "deadline";
            release();
          }, RECONCILE_DEADLINE_MS);

          Promise.resolve()
            .then(function () {
              return runWithCheckoutOwnership(attempt, function () {
                return mutex.enqueue(function () {
                  requireOperation(attempt, "theme-mutex-entry");
                  return reconcile(attempt);
                }).then(function (value) {
                  // Publish the released lease before navigator.locks hands
                  // ownership to another tab.
                  return Promise.resolve(release()).then(function () { return value; });
                }, function (error) {
                  attempt.errorCategory = coordinatorErrorCategory(error);
                  return Promise.resolve(release()).then(function () { throw error; });
                });
              });
            })
            .then(release, function (error) {
              attempt.errorCategory = coordinatorErrorCategory(error);
              return release();
            });
        } catch (outer) {
          // Once preventDefault has happened, a synchronous error must use the
          // installed release path rather than strand checkout.
          try {
            if (attempt) attempt.errorCategory = coordinatorErrorCategory(outer);
            if (release) release();
            else if (intercepted) directCheckoutNavigation();
          } catch (e) {
            if (intercepted) directCheckoutNavigation();
          }
        }
      }, true);
    } catch (e) {
      /* listener registration must never break the page */
    }
    try {
      window.addEventListener("pagehide", function () {
        if (activeAttempt) {
          if (activeAttempt.addDispatched && !activeAttempt.addResponseSettled) {
            activeAttempt.uncertainAdd = true;
            returnCheckAfterLease = true;
          }
          // An interrupted PREPARING owner remains active until expiry. Only
          // release() may publish released=true and authorize removal repair.
          extendReleaseLease(activeAttempt);
        }
      });
    } catch (e) {}

    // ---- Orphan reconcile ------------------------------------------------
    // When the last real subscription line leaves the cart, the hidden free
    // line must go too: otherwise a plan-carrying line no one can see keeps
    // flipping the cart's "has subscription" state for a one-time cart.
    // The resulting GA4 drawer_remove_from_cart (renamed 2026-08-24, audit
    // fix 4) is suppressed by the free-shipping
    // exclusion in snippets/cart-drawer-analytics.liquid.
    // 🔴 THIS PATH MUST NOT TOUCH THE NETWORK SPECULATIVELY. It runs on every
    // cart mutation, so an unconditional /cart.js read here costs one extra
    // request per quantity tap on the customer's critical path, keeps the cart
    // from ever going quiet, and burns storefront rate limit. Read ONLY when we
    // already have reason to believe a hidden line exists.
    //
    // A stale summary can therefore skip a sweep. That is safe: the checkout
    // interceptor reconciles from a fresh /cart.js read at submit time, which
    // is the only moment the invariant has to hold.
    var pending = null;
    function scheduleOrphanSweep(cart) {
      // Same frame gate as the return sweep: the orphan sweep is the other
      // page-lifecycle path that removes lines, and a nested instance must
      // never take it either.
      if (!isTopLevelDocument()) return;
      if (phase !== BROWSING || activeAttempt || buyboxBusy() || leaseSnapshot().lease) {
        returnSweepPending = true;
        return;
      }
      // Gate on `count` ONLY. subLines is precisely the value that just
      // changed, so treating a stale subLines>0 as "nothing to do" would skip
      // the sweep in the one case it exists for. With no hidden line in the
      // cart — the state of essentially every browsing session — this returns
      // before issuing any request at all.
      if (cart) {
        var eventHasSystemLine = ((cart && cart.items) || []).some(isFreeShipping);
        if (!eventHasSystemLine) return;
      } else if (summary.count === 0) return;
      clearTimeout(pending);
      pending = setTimeout(function () {
        if (phase !== BROWSING || activeAttempt || buyboxBusy() || leaseSnapshot().lease) {
          returnSweepPending = true;
          return;
        }
        var mutex = window.themeCartMutex;
        if (!mutex || !mutex.enqueue) return;
        runWithCleanupOwnership(function (cleanup) {
          return mutex.enqueue(function () {
            if (buyboxBusy()) { returnSweepPending = true; return; }
            requireOperation(cleanup, "orphan-sweep-start");
            // The event cart is only a zero-request gate. Truth is re-read
            // under both locks immediately before any cleanup write.
            return readCart(cleanup).then(function (current) {
              requireOperation(cleanup, "orphan-sweep-summary");
              var state = summarize(current);
              summary = state;
              if (lineBelongs(state) || state.lines.length === 0) return null;
              return removeLines(state.lines, cleanup)
                .then(function () { return readCart(cleanup); })
                .then(function (after) {
                  requireOperation(cleanup, "orphan-sweep-verify");
                  summary = summarize(after);
                  if (summary.lines.length > 0) throw new Error("orphan-sweep-verification");
                  document.dispatchEvent(new CustomEvent("cart:refresh"));
                });
            });
          });
        }).catch(function (error) {
          if (error && (error.staleAttempt || error.staleCleanup)) {
            setTimeout(function () { scheduleOrphanSweep(null); }, RETURN_GRACE_MS);
          }
          /* Other failures are best effort; checkout re-reconciles. */
        });
      }, 250);
    }

    try {
      document.addEventListener("cart:change", function (event) {
        try { noteEventCart(event); } catch (e) {}
        try { scheduleOrphanSweep(event && event.detail && event.detail.cart); } catch (e) {}
      });
      // The theme's own re-reads (drawer open, focus, bfcache) announce the
      // cart here; only the telemetry cart view listens.
      document.addEventListener("cart:refresh", function (event) {
        try { noteEventCart(event); } catch (e) {}
      });
      document.addEventListener("cart-drawer:refreshed", function () {
        try { scheduleOrphanSweep(null); } catch (e) {}
      });
    } catch (e) {}
})();
